import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, redirect, RouterProvider, useLoaderData } from 'react-router';
import './fonts.css';
import './styles.css';
import type { MeResponse } from '@hefesto/contracts';
import { Shell } from './layout/Shell';
import { PORTAL_ROUTE_ID } from './lib/portal';
import { requireSession, SESSION_ROUTE_ID } from './lib/session';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * Rutas (docs/ARCHITECTURE.md §7). Todas las páginas se cargan PEREZOSAMENTE: el JS inicial
 * solo lleva React, el router, el shell y el cliente de sesión.
 *   /login, /invite/:token, /forgot-password, /reset-password   Identidad (01A)
 *   /, /estado                                                  Área autenticada mínima (01A)
 *   /admin/*  Superadmin (02A): panel, clientes, planes, seguridad (MFA); (02B) soporte, equipo,
 *             accesos a datos, auditoría, sistema, agentes y claves
 *   /select-tenant    Selector de empresa (03A)
 *   /t/:tenantSlug/*  Portal (03A): Tus herramientas, empresa, equipo, roles, claves API;
 *                     (03B) soporte, avisos, actividad, integraciones, configuración;
 *                     (04A) /t/:tenantSlug/<herramienta> (página genérica del módulo);
 *                     (04B) /t/:tenantSlug/aprobaciones y /admin/aprobaciones;
 *                     (05B2) /t/:tenantSlug/integraciones/whatsapp
 *   /inbox/:tenantSlug/:conversationId?  (05B2) Inbox de WhatsApp en PESTAÑA PROPIA: chunk perezoso
 *             propio, sin el shell del Portal; acepta el enlace de los avisos de derivación.
 *   /admin/whatsapp  (05B2) saldos de las cuentas de YCloud y consumo mensual por empresa
 *   /admin/agente    (05F) Jarvis, el agente de Hefesto por Telegram: vincular, vínculos, consumo
 * El área /admin y el Portal (con TanStack Query y Radix) solo se descargan al entrar en ellos.
 */
/**
 * (05D) Después de un despliegue, una pestaña abierta con la versión anterior pide chunks que ya no
 * existen ("Failed to fetch dynamically imported module"). Vite avisa con `vite:preloadError`: se
 * recarga UNA vez para tomar la versión nueva; si vuelve a fallar enseguida, se deja ver el error
 * (nunca un bucle de recargas).
 */
const CHUNK_RELOAD_AT = 'hefesto:chunk-reload-at';
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_AT) ?? 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(CHUNK_RELOAD_AT, String(Date.now()));
  } catch {
    // Sin sessionStorage (modo privado estricto): se recarga igual.
  }
  event.preventDefault();
  window.location.reload();
});

function SessionLayout() {
  const me = useLoaderData() as MeResponse;
  return (
    <Shell me={me}>
      <Outlet />
    </Shell>
  );
}

const admin = {
  layout: () => import('./pages/admin/AdminLayout').then((m) => ({ Component: m.AdminLayout })),
  dashboard: () =>
    import('./pages/admin/DashboardPage').then((m) => ({ Component: m.DashboardPage })),
  clients: () => import('./pages/admin/ClientsPage').then((m) => ({ Component: m.ClientsPage })),
  client: () => import('./pages/admin/ClientPage').then((m) => ({ Component: m.ClientPage })),
  plans: () => import('./pages/admin/PlansPage').then((m) => ({ Component: m.PlansPage })),
  plan: () => import('./pages/admin/PlansPage').then((m) => ({ Component: m.PlanPage })),
  security: () => import('./pages/admin/SecurityPage').then((m) => ({ Component: m.SecurityPage })),
  // 02B
  support: () =>
    import('./pages/admin/SupportInboxPage').then((m) => ({ Component: m.SupportInboxPage })),
  ticket: () => import('./pages/admin/TicketPage').then((m) => ({ Component: m.TicketPage })),
  team: () => import('./pages/admin/TeamPage').then((m) => ({ Component: m.TeamPage })),
  access: () => import('./pages/admin/AccessPage').then((m) => ({ Component: m.AccessPage })),
  audit: () => import('./pages/admin/AuditPage').then((m) => ({ Component: m.AuditPage })),
  system: () => import('./pages/admin/SystemPage').then((m) => ({ Component: m.SystemPage })),
  serviceAccounts: () =>
    import('./pages/admin/ServiceAccountsPage').then((m) => ({
      Component: m.ServiceAccountsPage,
    })),
  // 04B
  approvals: () =>
    import('./pages/admin/ApprovalsPage').then((m) => ({ Component: m.ApprovalsPage })),
  // 05B2
  whatsapp: () =>
    import('./pages/admin/WhatsAppAdminPage').then((m) => ({ Component: m.WhatsAppAdminPage })),
  // 05F
  agent: () => import('./pages/admin/AgentPage').then((m) => ({ Component: m.AgentPage })),
};

/**
 * (03A) Portal del cliente: el layout trae el contexto de la empresa (loader) y su propio cliente
 * de consultas; cada sección es un chunk. `id: 'portal'` lo lee el shell para la navegación.
 */
const portal = {
  layout: () =>
    import('./pages/portal/PortalLayout').then((m) => ({
      Component: m.PortalLayout,
      loader: m.portalLoader,
      ErrorBoundary: m.PortalErrorBoundary,
    })),
  tools: () => import('./pages/portal/ToolsPage').then((m) => ({ Component: m.ToolsPage })),
  company: () => import('./pages/portal/CompanyPage').then((m) => ({ Component: m.CompanyPage })),
  team: () => import('./pages/portal/TeamPage').then((m) => ({ Component: m.TeamPage })),
  roles: () => import('./pages/portal/RolesPage').then((m) => ({ Component: m.RolesPage })),
  keys: () => import('./pages/portal/ApiKeysPage').then((m) => ({ Component: m.ApiKeysPage })),
  // 03B
  support: () => import('./pages/portal/SupportPage').then((m) => ({ Component: m.SupportPage })),
  newTicket: () =>
    import('./pages/portal/NewTicketPage').then((m) => ({ Component: m.NewTicketPage })),
  ticket: () => import('./pages/portal/TicketPage').then((m) => ({ Component: m.TicketPage })),
  notifications: () =>
    import('./pages/portal/NotificationsPage').then((m) => ({ Component: m.NotificationsPage })),
  activity: () =>
    import('./pages/portal/ActivityPage').then((m) => ({ Component: m.ActivityPage })),
  integrations: () =>
    import('./pages/portal/IntegrationsPage').then((m) => ({ Component: m.IntegrationsPage })),
  settings: () =>
    import('./pages/portal/SettingsPage').then((m) => ({ Component: m.SettingsPage })),
  // 04A: página genérica de una herramienta (módulo) habilitada para la empresa.
  module: () => import('./pages/portal/ModulePage').then((m) => ({ Component: m.ModulePage })),
  // 04B: aprobaciones de acciones de agentes.
  approvals: () =>
    import('./pages/portal/ApprovalsPage').then((m) => ({ Component: m.ApprovalsPage })),
  // 05B2: WhatsApp (conectar el número, ajustes, plantillas, consumo).
  whatsapp: () =>
    import('./pages/portal/WhatsAppPage').then((m) => ({ Component: m.WhatsAppPage })),
};

/** Mientras carga una página o se valida la sesión: solo la línea al rojo, sin saltos de layout. */
function PageFallback() {
  return (
    <div role="status" className="min-h-dvh bg-carbon">
      <div
        aria-hidden
        className="forge-heat h-[3px] bg-linear-to-r from-brasa-honda via-brasa to-[#ffb36b]"
      />
      <span className="sr-only">Cargando</span>
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    HydrateFallback: PageFallback,
    lazy: () => import('./pages/auth/LoginPage').then((m) => ({ Component: m.LoginPage })),
  },
  {
    path: '/invite/:token',
    HydrateFallback: PageFallback,
    lazy: () => import('./pages/auth/InvitePage').then((m) => ({ Component: m.InvitePage })),
  },
  {
    path: '/forgot-password',
    HydrateFallback: PageFallback,
    lazy: () =>
      import('./pages/auth/ForgotPasswordPage').then((m) => ({ Component: m.ForgotPasswordPage })),
  },
  {
    path: '/reset-password',
    HydrateFallback: PageFallback,
    lazy: () =>
      import('./pages/auth/ResetPasswordPage').then((m) => ({ Component: m.ResetPasswordPage })),
  },
  {
    // (05B2) Inbox: fuera del shell de la sesión (pestaña propia); su loader exige la sesión.
    path: '/inbox/:tenantSlug/:conversationId?',
    HydrateFallback: PageFallback,
    lazy: () =>
      import('./pages/inbox/InboxPage').then((m) => ({
        Component: m.InboxPage,
        loader: m.inboxLoader,
        ErrorBoundary: m.InboxErrorBoundary,
      })),
  },
  {
    id: SESSION_ROUTE_ID,
    loader: requireSession,
    element: <SessionLayout />,
    HydrateFallback: PageFallback,
    children: [
      {
        index: true,
        lazy: () => import('./pages/HomePage').then((m) => ({ Component: m.HomePage })),
      },
      {
        path: 'select-tenant',
        lazy: () =>
          import('./pages/SelectTenantPage').then((m) => ({ Component: m.SelectTenantPage })),
      },
      {
        id: PORTAL_ROUTE_ID,
        path: 't/:tenantSlug',
        lazy: portal.layout,
        children: [
          { index: true, lazy: portal.tools },
          { path: 'empresa', lazy: portal.company },
          { path: 'equipo', lazy: portal.team },
          { path: 'roles', lazy: portal.roles },
          { path: 'claves', lazy: portal.keys },
          { path: 'soporte', lazy: portal.support },
          { path: 'soporte/nuevo', lazy: portal.newTicket },
          { path: 'soporte/:ticketId', lazy: portal.ticket },
          { path: 'avisos', lazy: portal.notifications },
          { path: 'actividad', lazy: portal.activity },
          { path: 'integraciones', lazy: portal.integrations },
          { path: 'integraciones/whatsapp', lazy: portal.whatsapp },
          { path: 'configuracion', lazy: portal.settings },
          { path: 'aprobaciones', lazy: portal.approvals },
          // Enlaces de avisos anteriores a 03B (`/t/<slug>/support`).
          {
            path: 'support/*',
            loader: ({ params }) =>
              redirect(`/t/${params.tenantSlug}/soporte${params['*'] ? `/${params['*']}` : ''}`),
          },
          // (04A) Herramientas: `/t/:empresa/<slug>` (los segmentos de arriba, más específicos,
          // ganan; ningún slug de módulo puede coincidir con ellos: RESERVED_NAMESPACES).
          { path: ':moduleSlug/*', lazy: portal.module },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      {
        path: 'estado',
        lazy: () => import('./pages/FoundationPage').then((m) => ({ Component: m.FoundationPage })),
      },
      {
        path: 'admin',
        lazy: admin.layout,
        children: [
          { index: true, lazy: admin.dashboard },
          { path: 'clients', lazy: admin.clients },
          { path: 'clients/:ref', lazy: admin.client },
          { path: 'plans', lazy: admin.plans },
          { path: 'plans/:key', lazy: admin.plan },
          { path: 'security', lazy: admin.security },
          { path: 'support', lazy: admin.support },
          { path: 'support/:id', lazy: admin.ticket },
          { path: 'team', lazy: admin.team },
          { path: 'access', lazy: admin.access },
          { path: 'audit', lazy: admin.audit },
          { path: 'system', lazy: admin.system },
          { path: 'service-accounts', lazy: admin.serviceAccounts },
          { path: 'aprobaciones', lazy: admin.approvals },
          { path: 'whatsapp', lazy: admin.whatsapp },
          { path: 'agente', lazy: admin.agent },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
